/**
 * @file Saga setting or clearing a contract's end date: a 200 updates the store and snackbars, a 207 opens the end's partial dialog.
 * Failure path: catch → ErrorHandlerSequence with the route's expected errors → GLOBAL_CONTRACT_END_FAILURE.
 */
import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ErrorHandlerSequence, ExpectedErrors } from "../Sequences/ErrorHandlerSequence";
import Http from "../../Http/Http";
import Time from "../../Time/Time";
import ContractFactory from "../../Entity/Factories/ContractFactory";
import { ActionInterface } from "../../Interface/ActionInterface";
import { ContractInterface } from "../../Entity/Entities/Contract/ContractInterface";
import { isContractIdOnly } from "../../Entity/Entities/Contract/PartialAnswer";
import Formatter from "../../Localization/Formatter";
import Translator from "../../Translation/Translator";

const EXPECTED_ERRORS: ExpectedErrors = {
  be_ajv_not_validated: null,
  financial_operations_were_blocked: { titleKey: "refusal-title-contract-end", bodyKey: "refusal-body-financial-processing-busy" },
  be_contract_not_found: { titleKey: "refusal-title-contract-end", bodyKey: "refusal-body-contract-end-not-found" },
  cannot_set_end_date_to_unsigned_contract: { titleKey: "refusal-title-contract-end", bodyKey: "refusal-body-contract-end-unsigned" },
  be_server_error: { titleKey: "refusal-title-contract-end", bodyKey: "refusal-body-contract-end-server-error" },
  be_contract_end_date_earlier_than_firstDate: { titleKey: "refusal-title-contract-end", bodyKey: "be_contract_end_date_earlier_than_firstDate" },
  cannot_set_end_date_renewed: { titleKey: "refusal-title-contract-end", bodyKey: "cannot_set_end_date_renewed" },
  be_contract_end_failed: { titleKey: "refusal-title-contract-end", bodyKey: "be_contract_end_failed" }
};

export default function* contractEnd(action: ActionInterface): any {
  try {
    yield put({
      type: Constants.GLOBAL_CONTRACT_END_REQUEST
    });
    const res = yield call(Http.put, `contract/${action.payload?.contractId}/end`, {
      endDate: Time.formatOrNull(action.payload?.endDate)
    });

    if (!isContractIdOnly(res.data.data)) {
      const contract: ContractInterface = ContractFactory.create(res.data.data.contract);
      yield put({
        type: Constants.GLOBAL_CONTRACT_END_SUCCESS,
        payload: {
          contract: contract
        }
      });

      if (res.status !== 207) {
        yield put({
          type: Constants.SNACKBAR_ADD_MESSAGE,
          payload: {
            message: getSuccessMessageForContract(contract),
            type: "success"
          }
        });
      }
    }

    if (res.status === 207) {
      yield put({
        type: Constants.GLOBAL_REFUSAL_DIALOG_OPEN,
        payload: {
          titleKey: "partial-title-contract-end",
          bodyKey: "partial-body-contract-end"
        }
      });
    }

    if (action.hasOwnProperty("onSuccess") === true) {
      action.onSuccess.call(this, res.data);
    }
  } catch (err) {
    yield ErrorHandlerSequence(err, action, EXPECTED_ERRORS);

    yield put({
      type: Constants.GLOBAL_CONTRACT_END_FAILURE,
      payload: {
        err: err
      }
    });
  }
}

function getSuccessMessageForContract(contract: ContractInterface): string {
  if (!contract.endDate) {
    return Translator.trans("snackbar-message-contract-end-date-updated-successfully");
  }

  // The route answers Ended or Closed for a date that has passed; any other status means the date is still ahead.
  return Translator.trans(
    contract.isEnded || contract.isClosed ? "snackbar-message-contract-ended-successfully" : "snackbar-message-contract-ending-successfully",
    { endDate: Formatter.formatDate(contract.endDate) }
  );
}

export function* watchContractEnd() {
  yield takeEvery(Constants.GLOBAL_CONTRACT_END_START, contractEnd);
}
